import { Fragment, useEffect, type ReactNode } from 'react'
import { useI18n } from '../lib/i18n'
import { LEGAL_EFFECTIVE, LEGAL_ENTITY, legalPlaceholders } from '../legal/entity'
import {
  LEGAL_DOCS,
  LEGAL_GROUPS,
  legacyLegalTarget,
  legalPathFor,
  legalRouteFromPath,
  type LegalLanguage,
  type LegalRoute,
} from '../legal/registry'
import type { LegalBlock, LegalSlug } from '../legal/types'

/**
 * Los documentos legales: términos, privacidad, uso aceptable, cookies, facturación,
 * ventas, dominios, IA, beta, derechos de autor, procesamiento de datos y
 * subprocesadores.
 *
 * El texto de cada documento vive en `legal/docs`, con su versión en cada idioma
 * (ver `legal/types`); aquí sólo se pinta. Lo pequeño de la página —rótulos,
 * botones— sale del catálogo como el resto del panel.
 *
 * `/legal` es el índice, y cada documento tiene su propia dirección: Google pide
 * una para la política de privacidad al verificar una app, y quien lee unos
 * términos no quiere buscarlos entre otros diez textos.
 */

/** Las direcciones que ya existían y que la aplicación debe seguir sirviendo. */
export { legalRouteFromPath }
export type { LegalRoute }

/** Cambia `{operator}`, `{email}`… por los valores que los documentos citan sin escribirlos. */
function fill(text: string, vars: Record<string, string>): string {
  return text.replace(/\{(\w+)\}/g, (match, key: string) => vars[key] ?? match)
}

/**
 * Negrita y enlaces dentro de un texto: `**así**` y `[así](enlace)`. Un enlace
 * `doc:privacy` se resuelve a la dirección de ese documento en el idioma de la
 * página, para que renombrar una dirección no obligue a tocar los textos.
 */
function inline(text: string, language: LegalLanguage): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).filter(Boolean).map((part, index) => {
    const bold = /^\*\*([^*]+)\*\*$/.exec(part)
    if (bold) return <strong key={index}>{bold[1]}</strong>
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part)
    if (link) {
      const target = link[2]!
      const href = target.startsWith('doc:') && target.slice(4) in LEGAL_DOCS
        ? legalPathFor(target.slice(4) as LegalSlug, language)
        : target
      return <a key={index} href={href}>{link[1]}</a>
    }
    return <Fragment key={index}>{part}</Fragment>
  })
}

function Block({ block, language, vars }: { block: LegalBlock; language: LegalLanguage; vars: Record<string, string> }) {
  if (typeof block === 'string') return <p>{inline(fill(block, vars), language)}</p>
  if ('list' in block) {
    return <ul>{block.list.map((item, i) => <li key={i}>{inline(fill(item, vars), language)}</li>)}</ul>
  }
  if ('ordered' in block) {
    return <ol>{block.ordered.map((item, i) => <li key={i}>{inline(fill(item, vars), language)}</li>)}</ol>
  }
  return (
    <div className="legal-table-wrap">
      <table className="legal-table">
        <thead>
          <tr>{block.table.head.map((cell, i) => <th key={i} scope="col">{fill(cell, vars)}</th>)}</tr>
        </thead>
        <tbody>
          {block.table.rows.map((row, r) => (
            <tr key={r}>{row.map((cell, c) => <td key={c}>{inline(fill(cell, vars), language)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LegalDocument({ slug, language }: { slug: LegalSlug; language: LegalLanguage }) {
  const { t } = useI18n()
  const doc = LEGAL_DOCS[slug].docs[language]
  const vars = legalPlaceholders(language)

  return (
    <article className="legal-document">
      <header className="legal-heading">
        <p>{t('legal.eyebrow')}</p>
        <h1>{doc.title}</h1>
        <span className="legal-summary">{doc.summary}</span>
        <span>{t('legal.effective', { date: LEGAL_EFFECTIVE[language], version: LEGAL_EFFECTIVE.version })}</span>
      </header>

      {doc.intro?.map((block, i) => <div className="legal-intro" key={i}><Block block={block} language={language} vars={vars} /></div>)}

      {doc.sections.map((section, index) => {
        const n = index + 1
        return (
          <section key={index} id={`section-${n}`} className="legal-section">
            <h2>{n}. {fill(section.title, vars)}</h2>
            {section.blocks?.map((block, i) => <Block key={i} block={block} language={language} vars={vars} />)}
            {section.subs?.map((sub, m) => (
              <div key={m} id={sub.title ? `section-${n}-${m + 1}` : undefined} className="legal-sub">
                {sub.title ? <h3>{n}.{m + 1} {fill(sub.title, vars)}</h3> : null}
                {sub.blocks.map((block, i) => <Block key={i} block={block} language={language} vars={vars} />)}
              </div>
            ))}
          </section>
        )
      })}
    </article>
  )
}

/** Todos los documentos legales, visibles junto al texto para saltar entre ellos. */
function LegalDocumentsNav({ active, language }: { active: LegalSlug; language: LegalLanguage }) {
  const { t } = useI18n()

  return (
    <nav className="legal-side-nav legal-docs-nav" aria-label={t('legal.hubTitle')}>
      <strong className="legal-side-nav-title">{t('legal.hubTitle')}</strong>
      {LEGAL_GROUPS.map((group) => (
        <div key={group.key} className="legal-side-nav-group">
          <span>{t(`legal.group.${group.key}`)}</span>
          <ul>
            {group.slugs.map((slug) => (
              <li key={slug}>
                <a
                  href={legalPathFor(slug, language)}
                  className={slug === active ? 'is-active' : undefined}
                  aria-current={slug === active ? 'page' : undefined}
                >
                  {LEGAL_DOCS[slug].docs[language].title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  )
}

/** Índice completo del documento, incluidos sus subapartados. */
function LegalContentsNav({ slug, language }: { slug: LegalSlug; language: LegalLanguage }) {
  const { t } = useI18n()
  const doc = LEGAL_DOCS[slug].docs[language]
  const vars = legalPlaceholders(language)

  return (
    <nav className="legal-side-nav legal-toc" aria-label={t('legal.toc')}>
      <strong className="legal-side-nav-title">{t('legal.toc')}</strong>
      <ol>
        {doc.sections.map((section, index) => {
          const n = index + 1
          const titledSubs = section.subs?.map((sub, subIndex) => ({ sub, subIndex })).filter(({ sub }) => sub.title) ?? []
          return (
            <li key={index}>
              <a href={`#section-${n}`}><span>{n}.</span>{fill(section.title, vars)}</a>
              {titledSubs.length > 0 ? (
                <ol>
                  {titledSubs.map(({ sub, subIndex }) => (
                    <li key={subIndex}>
                      <a href={`#section-${n}-${subIndex + 1}`}>
                        <span>{n}.{subIndex + 1}</span>{fill(sub.title!, vars)}
                      </a>
                    </li>
                  ))}
                </ol>
              ) : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

function LegalIndex({ language }: { language: LegalLanguage }) {
  const { t } = useI18n()
  return (
    <article className="legal-document">
      <header className="legal-heading">
        <p>{t('legal.eyebrow')}</p>
        <h1>{t('legal.hubTitle')}</h1>
        <span className="legal-summary">{t('legal.hubIntro')}</span>
        <span>{t('legal.effective', { date: LEGAL_EFFECTIVE[language], version: LEGAL_EFFECTIVE.version })}</span>
      </header>
      {LEGAL_GROUPS.map((group) => (
        <section key={group.key} className="legal-hub-group">
          <h2>{t(`legal.group.${group.key}`)}</h2>
          <div className="legal-hub-grid">
            {group.slugs.map((slug) => {
              const doc = LEGAL_DOCS[slug].docs[language]
              return (
                <a key={slug} className="legal-hub-card" href={legalPathFor(slug, language)}>
                  <strong>{doc.title}</strong>
                  <span>{doc.summary}</span>
                </a>
              )
            })}
          </div>
        </section>
      ))}
    </article>
  )
}

export function LegalPage({ route }: { route: LegalRoute }) {
  const { t, language, setLanguage } = useI18n()
  const lang: LegalLanguage = language === 'en' ? 'en' : 'es'

  /* Los enlaces viejos con ancla —`/legal#terminos`— se llevan al documento que
     ahora tiene esa sección, en vez de dejar al lector en el índice. */
  useEffect(() => {
    if (route.kind !== 'index') return
    const target = legacyLegalTarget(window.location.hash)
    if (target) window.location.replace(legalPathFor(target, lang))
  }, [route, lang])

  /* El título de la pestaña es el del documento: es lo que se ve al tener varios
     abiertos y lo que aparece al compartir o guardar la dirección. */
  const pageTitle = route.kind === 'doc' ? LEGAL_DOCS[route.slug].docs[lang].title : t('legal.hubTitle')
  useEffect(() => {
    const previous = document.title
    document.title = `${pageTitle} · véltica`
    return () => { document.title = previous }
  }, [pageTitle])

  return (
    <main className="legal-page">
      <header className="legal-topbar">
        <a href="https://app.veltica.com" aria-label={t('legal.goHome')}>
          <img src="/veltica.svg" alt="véltica" />
        </a>
        <div className="legal-topbar-actions">
          <div className="legal-lang" role="group" aria-label={t('legal.language')}>
            {(['es', 'en'] as const).map((code) => (
              <button key={code} type="button" className={code === lang ? 'is-active' : ''} onClick={() => setLanguage(code)} aria-pressed={code === lang}>
                {code.toUpperCase()}
              </button>
            ))}
          </div>
          <a className="legal-back-link" href="/login">{t('legal.back')}</a>
        </div>
      </header>

      {route.kind === 'doc' ? (
        <div className="legal-layout">
          <LegalDocumentsNav active={route.slug} language={lang} />
          <LegalDocument slug={route.slug} language={lang} />
          <LegalContentsNav slug={route.slug} language={lang} />
        </div>
      ) : <LegalIndex language={lang} />}

      <footer className="legal-doc-footer">
        {route.kind === 'doc' ? <a href="/legal">{t('legal.allDocs')}</a> : null}
        <span>
          {t('legal.contact.email', { email: '\u0000' }).split('\u0000').flatMap((chunk, index) => (index === 0
            ? [chunk]
            : [<a key="email" href={`mailto:${LEGAL_ENTITY.email}`}>{LEGAL_ENTITY.email}</a>, chunk]))}
        </span>
      </footer>
    </main>
  )
}
